import type { AlttvisionApi } from '@shared/types';
import { diagnoseBridgeEnvironment, type BridgeDiagnostic } from '@shared/bridge-contract';
import { getWebConsoleApi, isWebConsoleMode } from './web-console-api';

function createReadyWebConsoleDiagnostic(): BridgeDiagnostic {
  return {
    status: 'ready',
    expectedVersion: 'web-console',
    hasApi: true,
    hasBridge: true,
    isElectron: false,
    missingDomains: [],
    missingMethods: [],
    actualVersion: 'web-console'
  };
}

export function getDesktopBridgeDiagnostic(): BridgeDiagnostic {
  if (isWebConsoleMode()) {
    return createReadyWebConsoleDiagnostic();
  }

  if (typeof window === 'undefined') {
    return diagnoseBridgeEnvironment({
      api: undefined,
      bridge: undefined,
      userAgent: ''
    });
  }

  return diagnoseBridgeEnvironment({
    api: window.alttvision,
    bridge: window.alttvisionBridge,
    userAgent: window.navigator.userAgent
  });
}

export function getDesktopBridgeErrorMessage(diagnostic: BridgeDiagnostic): string {
  switch (diagnostic.status) {
    case 'browser': {
      const browserBridgeError =
        typeof window === 'undefined' ? null : window.alttvisionBrowserDevBridgeError;
      return [
        'Alttvision Desktop a fost deschis in browser, dar bridge-ul local de development nu este disponibil.',
        'Porneste npm run electron:dev si deschide http://127.0.0.1:53173 in Codex Browser.',
        browserBridgeError ? `Detalii: ${browserBridgeError}` : null
      ]
        .filter(Boolean)
        .join(' ');
    }
    case 'preload-missing':
      return 'Electron a pornit fereastra, dar preload-ul securizat nu a fost injectat. Reconstruieste procesul Electron si reporneste aplicatia.';
    case 'api-missing':
      return 'Preload-ul a pornit, dar API-ul desktop nu este disponibil in renderer. Reconstruieste si reporneste aplicatia.';
    case 'api-incomplete':
      return `Bridge-ul desktop este incomplet. Lipsesc: ${[
        ...diagnostic.missingDomains,
        ...diagnostic.missingMethods
      ].join(', ')}.`;
    case 'api-stale':
      return 'Preload-ul desktop pare vechi si nu expune contractul curent al aplicatiei. Reconstruieste si reporneste aplicatia.';
    case 'version-mismatch':
      return `Versiunea bridge-ului desktop nu se potriveste. Renderer: ${diagnostic.expectedVersion}, preload: ${
        diagnostic.actualVersion ?? 'necunoscuta'
      }. Reconstruieste si reporneste aplicatia.`;
    case 'ready':
      return '';
  }
}

export function getDesktopApi(): AlttvisionApi {
  if (isWebConsoleMode()) {
    return getWebConsoleApi();
  }

  const diagnostic = getDesktopBridgeDiagnostic();

  if (diagnostic.status === 'ready' && window.alttvision) {
    return window.alttvision;
  }

  throw new Error(getDesktopBridgeErrorMessage(diagnostic));
}
